import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Alert, CircularProgress, Typography,
  IconButton, Radio, RadioGroup, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import competitionService from '../services/competitionService';

const ManageCompetitions = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [competitions, setCompetitions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [competitionType, setCompetitionType] = useState('paid');
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    fromDate: '',
    fromTime: '',
    toDate: '',
    toTime: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    poolTokenAmount: 0,
    totalAmount: 0,
    awards: [80],
    serviceFee: 10,
    athleteFee: 10
  });
  const [createErrors, setCreateErrors] = useState({});

  const [showEndConfirmModal, setShowEndConfirmModal] = useState(false);
  const [selectedComp, setSelectedComp] = useState(null);
  const [isEnding, setIsEnding] = useState(false);

  useEffect(() => {
    dispatch(login(user));
    loadCompetitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function formatInTimeZone(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(date);
    const map = {};
    for (const p of parts) map[p.type] = p.value;
    return { date: `${map.year}-${map.month}-${map.day}`, time: `${map.hour}:${map.minute}` };
  }

  function getOffsetMinutes(date, timeZone) {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
    const parts = dtf.formatToParts(date);
    const map = {};
    for (const p of parts) map[p.type] = p.value;
    const asUTC = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), Number(map.hour), Number(map.minute), Number(map.second));
    return (asUTC - date.getTime()) / 60000;
  }

  function convertWallClockBetweenTimeZones(dateStr, timeStr, fromTz, toTz) {
    if (!dateStr || !timeStr || !fromTz || !toTz) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    if (![y, m, d, hh, mm].every((v) => Number.isFinite(v))) return null;
    const utcGuess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
    const offsetMin = getOffsetMinutes(utcGuess, fromTz);
    const instant = new Date(utcGuess.getTime() - offsetMin * 60000);
    return formatInTimeZone(instant, toTz);
  }

  const loadCompetitions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await competitionService.getCurrentCompetition();
      const list = Array.isArray(res?.result) ? res.result : res;
      const items = Array.isArray(list) ? list : [];
      const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const converted = items.map((item) => {
        const serverTz = item?.timezone;
        if (!serverTz || !clientTz) return item;
        const from = convertWallClockBetweenTimeZones(item.fromDate, item.fromTime, serverTz, clientTz);
        const to = convertWallClockBetweenTimeZones(item.toDate, item.toTime, serverTz, clientTz);
        if (!from || !to) return item;
        return { ...item, fromDate: from.date, fromTime: from.time, toDate: to.date, toTime: to.time };
      });
      setCompetitions(converted || []);
    } catch (err) {
      setError('Failed to load competitions.');
      setCompetitions([]);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndClick = (comp) => {
    setSelectedComp(comp);
    setShowEndConfirmModal(true);
  };

  const handleEndConfirm = async () => {
    if (!selectedComp || isEnding) return;
    setIsEnding(true);
    setError(null);
    try {
      await competitionService.endCompetition({ competitionId: selectedComp._id });
      setShowEndConfirmModal(false);
      setSelectedComp(null);
      await loadCompetitions();
    } catch (err) {
      setError('Failed to end competition. Please try again.');
    } finally {
      setIsEnding(false);
    }
  };

  const handleCancelEnd = () => {
    if (isEnding) return;
    setShowEndConfirmModal(false);
    setSelectedComp(null);
  };

  const validateCreate = () => {
    const e = {};
    if (!createForm.title.trim()) e.title = 'Title is required';
    if (!createForm.description.trim()) e.description = 'Description is required';
    if (!createForm.fromDate) e.fromDate = 'Start date is required';
    if (!createForm.fromTime) e.fromTime = 'Start time is required';
    if (!createForm.toDate) e.toDate = 'End date is required';
    if (!createForm.toTime) e.toTime = 'End time is required';
    if (!createForm.timezone) e.timezone = 'Timezone is required';
    if (!createForm.athleteFee && competitionType === 'paid') e.athleteFee = 'Athlete share fee percentage is required';
    if (!createForm.serviceFee && competitionType === 'paid') e.serviceFee = 'Thravos share fee percentage is required';
    if (!createForm.poolTokenAmount && competitionType === 'paid') e.poolTokenAmount = 'Pool Enter Token Amount is required';
    if (!createForm.totalAmount && competitionType === 'free') e.totalAmount = 'Pool Enter Token Amount is required';

    let sum = 0;
    for (const a of createForm.awards) sum += Number(a || 0);
    if (sum !== 100 - createForm.athleteFee - createForm.serviceFee) {
      alert('Total reward percentage should be ' + (100 - createForm.athleteFee - createForm.serviceFee) + ' %');
      return false;
    }
    setCreateErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;
    setIsCreating(true);
    setError(null);
    try {
      await competitionService.createCompetition(createForm);
      setShowCreate(false);
      setCreateForm({
        title: '',
        description: '',
        fromDate: '',
        fromTime: '',
        toDate: '',
        toTime: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        totalAmount: 0,
        poolTokenAmount: 0,
        awards: [80],
        serviceFee: 10,
        athleteFee: 10
      });
      await loadCompetitions();
    } catch (err) { setError('Failed to create competition.'); }
    finally { setIsCreating(false); }
  };

  const addAwardRow = () => setCreateForm(prev => ({ ...prev, awards: [...prev.awards, 0] }));
  const removeAwardRow = (idx) => setCreateForm(prev => ({ ...prev, awards: prev.awards.filter((_, i) => i !== idx) }));
  const updateAward = (idx, val) => setCreateForm(prev => ({ ...prev, awards: prev.awards.map((a, i) => i === idx ? Number(val) : a) }));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <IconButton onClick={loadCompetitions} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
        <Button variant="contained" size="small" onClick={() => setShowCreate(v => !v)} startIcon={showCreate ? <CloseIcon /> : <AddIcon />}>
          {showCreate ? 'Hide Form' : 'Create Competition'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {showCreate && (
        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Create Competition</Typography>

          <RadioGroup row value={competitionType} onChange={(e) => {
            const v = e.target.value;
            setCompetitionType(v);
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (v === 'paid') setCreateForm(p => ({ ...p, poolTokenAmount: 0, awards: [80], serviceFee: 10, athleteFee: 10, timezone: p.timezone || tz }));
            else setCreateForm(p => ({ ...p, poolTokenAmount: 0, awards: [50, 30, 20], serviceFee: 0, athleteFee: 0, timezone: p.timezone || tz }));
          }} sx={{ mb: 2 }}>
            <FormControlLabel value="paid" control={<Radio />} label="Paid" />
            <FormControlLabel value="free" control={<Radio />} label="Free" />
          </RadioGroup>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Title" value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))} error={!!createErrors.title} helperText={createErrors.title} sx={{ flex: 1, minWidth: 200 }} />
            {competitionType === 'paid' ? (
              <TextField size="small" label="Pool Enter Token Amount" type="number" value={createForm.poolTokenAmount} onChange={e => setCreateForm(p => ({ ...p, poolTokenAmount: e.target.value }))} error={!!createErrors.poolTokenAmount} helperText={createErrors.poolTokenAmount} sx={{ width: 200 }} />
            ) : (
              <TextField size="small" label="Reward Token Amount" type="number" value={createForm.totalAmount} onChange={e => setCreateForm(p => ({ ...p, totalAmount: e.target.value }))} error={!!createErrors.totalAmount} helperText={createErrors.totalAmount} sx={{ width: 200 }} />
            )}
          </Box>

          <TextField size="small" label="Description" multiline rows={2} fullWidth value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} error={!!createErrors.description} helperText={createErrors.description} sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="From Date" type="date" value={createForm.fromDate} onChange={e => setCreateForm(p => ({ ...p, fromDate: e.target.value }))} InputLabelProps={{ shrink: true }} error={!!createErrors.fromDate} helperText={createErrors.fromDate} sx={{ width: 160 }} />
            <TextField size="small" label="From Time" type="time" value={createForm.fromTime} onChange={e => setCreateForm(p => ({ ...p, fromTime: e.target.value }))} InputLabelProps={{ shrink: true }} error={!!createErrors.fromTime} helperText={createErrors.fromTime} sx={{ width: 140 }} />
            <TextField size="small" label="To Date" type="date" value={createForm.toDate} onChange={e => setCreateForm(p => ({ ...p, toDate: e.target.value }))} InputLabelProps={{ shrink: true }} error={!!createErrors.toDate} helperText={createErrors.toDate} sx={{ width: 160 }} />
            <TextField size="small" label="To Time" type="time" value={createForm.toTime} onChange={e => setCreateForm(p => ({ ...p, toTime: e.target.value }))} InputLabelProps={{ shrink: true }} error={!!createErrors.toTime} helperText={createErrors.toTime} sx={{ width: 140 }} />
          </Box>

          <FormControl size="small" sx={{ minWidth: 220, mb: 2 }} error={!!createErrors.timezone}>
            <InputLabel>Timezone</InputLabel>
            <Select
              label="Timezone"
              value={createForm.timezone}
              onChange={e => setCreateForm(p => ({ ...p, timezone: e.target.value }))}
            >
              <MenuItem value="UTC">UTC</MenuItem>
              <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
              <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
              <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
              <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
              <MenuItem value="Europe/London">London (GMT)</MenuItem>
              <MenuItem value="Europe/Paris">Paris (CET)</MenuItem>
              <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
              <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
              <MenuItem value="Australia/Sydney">Sydney (AEDT)</MenuItem>
            </Select>
            {createErrors.timezone && <Typography variant="caption" color="error">{createErrors.timezone}</Typography>}
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField size="small" label="Athlete Fee" type="number" value={createForm.athleteFee} onChange={e => setCreateForm(p => ({ ...p, athleteFee: Number(e.target.value) }))} error={!!createErrors.athleteFee} helperText={createErrors.athleteFee} sx={{ width: 140 }} />
            <TextField size="small" label="Thravos Fee" type="number" value={createForm.serviceFee} onChange={e => setCreateForm(p => ({ ...p, serviceFee: Number(e.target.value) }))} error={!!createErrors.serviceFee} helperText={createErrors.serviceFee} sx={{ width: 140 }} />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Award Distribution</Typography>
            <Button size="small" onClick={addAwardRow} startIcon={<AddIcon />}>Add Row</Button>
          </Box>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow><TableCell>Rank</TableCell><TableCell>Reward %</TableCell><TableCell width={80}></TableCell></TableRow>
            </TableHead>
            <TableBody>
              {createForm.awards.map((a, i) => (
                <TableRow key={i}>
                  <TableCell>Top {i + 1}</TableCell>
                  <TableCell><TextField size="small" type="number" value={a} onChange={e => updateAward(i, e.target.value)} sx={{ width: 100 }} /></TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeAwardRow(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Button variant="contained" onClick={handleCreate} disabled={isCreating}>
            {isCreating ? 'Starting...' : 'Start Competition'}
          </Button>
        </Paper>
      )}

      {!showCreate && (
        <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Current TRVS</TableCell>
                  <TableCell>Winners</TableCell>
                  <TableCell>Service Fee</TableCell>
                  <TableCell>Athlete Fee</TableCell>
                  <TableCell>Pool Token</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {competitions.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#8b949e' }}>No active competitions</TableCell></TableRow>
                ) : competitions.map(c => (
                  <TableRow key={c._id}>
                    <TableCell sx={{ fontWeight: 600 }}>{c.title}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{c.fromDate} {c.fromTime} → {c.toDate} {c.toTime}</TableCell>
                    <TableCell>{c.totalAmount}</TableCell>
                    <TableCell>{c.awards?.length}</TableCell>
                    <TableCell>{c.serviceFee}</TableCell>
                    <TableCell>{c.athleteFee}</TableCell>
                    <TableCell>{c.poolTokenAmount}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        onClick={() => handleEndClick(c)}
                        disabled={isEnding}
                        sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}
                      >
                        {isEnding && selectedComp?._id === c._id ? 'Ending...' : 'End'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={showEndConfirmModal} onClose={handleCancelEnd} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm End Competition</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This action cannot be undone.</Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>You are about to end:</Typography>
          {selectedComp && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2"><strong>Title:</strong> {selectedComp.title}</Typography>
              <Typography variant="body2"><strong>Period:</strong> {selectedComp.fromDate} {selectedComp.fromTime} → {selectedComp.toDate} {selectedComp.toTime}</Typography>
              <Typography variant="body2"><strong>Total Amount:</strong> {selectedComp.totalAmount} TRVS</Typography>
              <Typography variant="body2"><strong>Winners:</strong> {selectedComp.awards?.length}</Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Ending will distribute tokens to winners, close the competition, and notify participants.
          </Alert>
          <Typography variant="body2" color="error">Are you sure you want to end this competition?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEnd} disabled={isEnding}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleEndConfirm} disabled={isEnding}>
            {isEnding ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Yes, End Competition'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageCompetitions;
